import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { Link } from "react-router-dom"
import CreatableSelect from "react-select/creatable"
import Table from "../../../../core/common/dataTable/index"
import CommonSelect, { Option } from "../../../../core/common/commonSelect"
import { all_routes } from "../../../router/all_routes"
import { selectCurrentSession } from "../../../../core/data/redux/slice/sessionsSlice"
import { selectSystemsForCurrentBranch } from "../../../../core/data/redux/slice/systemsSlice"
import { getAllClasses } from "../../../../service/classes"
import { listGroups } from "../../../../service/group"
import { getAllSessions } from "../../../../service/sessions"
import { getEnrollments, Enrollment } from "../../../../service/enrollment"
import { promoteBulk } from "../../../../service/promotion"

const BLANK: Option = { label: "Select", value: "" }

// No pagination on this screen — the whole matching roster loads at once.
const ALL_STUDENTS_LIMIT = 2000

const toOptions = (arr: any[] = []): Option[] =>
  (arr || []).map((x) => ({ value: x._id, label: x.name }))

const StudentPromotion = () => {
  const routes = all_routes
  const currentSession = useSelector(selectCurrentSession)
  const systems = useSelector(selectSystemsForCurrentBranch)

  // ── System (Matriculation / O-A Levels) ──────────────────────────
  const [systemId, setSystemId] = useState<string>("")
  const systemOptions: Option[] = (systems || []).map((s: any) => ({ value: s._id, label: s.name }))
  // Default to the first system once systems load.
  useEffect(() => {
    if (!systemId && systems?.[0]?._id) setSystemId(systems[0]._id)
  }, [systems, systemId])

  // ── Source filters (current session) ──────────────────────────────
  // Full class objects (each carries a `sections` array) — kept so the promote
  // modal can offer a target class's own sections.
  const [classesRaw, setClassesRaw] = useState<any[]>([])
  const [classOptions, setClassOptions] = useState<Option[]>([])
  const [groupOptions, setGroupOptions] = useState<Option[]>([])
  const [loadingGroups, setLoadingGroups] = useState(false)

  // Pending selections shown in the toolbar — System and Class are required,
  // Section and Group are optional. Nothing is fetched until Search is
  // clicked; see `hasSearched` below.
  const [draftClassId, setDraftClassId] = useState("")
  const [draftSectionId, setDraftSectionId] = useState("")
  const [draftGroupId, setDraftGroupId] = useState("")

  const [appliedFilters, setAppliedFilters] = useState({
    classId: "",
    sectionId: "",
    groupId: "",
  })

  // No data is shown until the admin explicitly clicks Search.
  const [hasSearched, setHasSearched] = useState(false)

  const [students, setStudents] = useState<Enrollment[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  // The Table's search box only calls this back — filtering the rows is on us.
  const [searchText, setSearchText] = useState("")
  const filteredStudents = useMemo(() => {
    const q = searchText.trim().toLowerCase()
    if (!q) return students
    return students.filter((r) => {
      const gr = (r.student?.grNumber || "").toLowerCase()
      const name = `${r.user?.firstName || ""} ${r.user?.lastName || ""}`.toLowerCase()
      return gr.includes(q) || name.includes(q)
    })
  }, [students, searchText])

  // ── Promote modal (target) ────────────────────────────────────────
  const [showModal, setShowModal] = useState(false)
  const [sessionOptions, setSessionOptions] = useState<Option[]>([])
  const [tClassOptions, setTClassOptions] = useState<Option[]>([])
  const [tSectionOptions, setTSectionOptions] = useState<Option[]>([])
  const [tGroupOptions, setTGroupOptions] = useState<Option[]>([])
  const [tSession, setTSession] = useState<Option>(BLANK)
  const [tClass, setTClass] = useState<Option>(BLANK)
  const [tSection, setTSection] = useState<Option>(BLANK)
  const [tGroup, setTGroup] = useState<Option>(BLANK)
  const [promoting, setPromoting] = useState(false)
  // Set when the backend reports some/all selected students are already
  // enrolled in the target session — nothing has been written yet. Confirming
  // re-sends the same promote payload with confirmUpdate:true.
  const [confirmPromote, setConfirmPromote] = useState<{
    message: string
    details: {
      enrollmentId: string
      name: string
      grNumber: string
      targetClass?: string
      targetSection?: string
      targetGroup?: string | null
    }[]
    payload: Parameters<typeof promoteBulk>[0]
  } | null>(null)

  // Load source classes + target sessions.
  useEffect(() => {
    if (!currentSession?._id) return
    ;(async () => {
      try {
        const [cls, sess] = await Promise.all([
          getAllClasses({ session: currentSession._id, system: systemId, limit: 200 } as any),
          // Current campus's sessions only — `all: true` would return every
          // campus's copy and show duplicate titles (and picking another
          // campus's session leaves the Target Section empty).
          getAllSessions({ limit: 200 } as any),
        ])
        const rawClasses = (cls as any)?.classes || (cls as any)?.data || []
        setClassesRaw(rawClasses)
        setClassOptions(toOptions(rawClasses))
        setSessionOptions(((sess as any)?.sessions || []).map((s: any) => ({ value: s._id, label: s.title })))
      } catch {
        /* fetch failures surface via the list toast below */
      }
    })()
  }, [currentSession?._id, systemId])

  // Section names aren't globally unique — the same "A" can belong to
  // different classes as entirely different Section documents. Fetching
  // sections session-wide (not scoped to the chosen class) let the dropdown
  // show an "A" whose _id didn't match the enrolled students' actual section,
  // so filtering by it silently returned nothing. Derive options from the
  // SELECTED CLASS's own `sections` array instead (same source the target
  // side already uses correctly). A memo (not a state+effect) so the options
  // update in the same render as the class change — no stale-options flash,
  // and no section is ever left selected from the previous class.
  const sectionOptions: Option[] = useMemo(() => {
    const cls = classesRaw.find((c) => c._id === draftClassId)
    return toOptions((cls?.sections as any[]) || [])
  }, [classesRaw, draftClassId])

  // Group only applies to classes that actually have groups (Grade 9+) — load
  // it once a class is picked, same lookup the target side already uses.
  useEffect(() => {
    if (!draftClassId) {
      setGroupOptions([])
      return
    }
    ;(async () => {
      try {
        setLoadingGroups(true)
        const grp = await listGroups({ system: systemId, class: draftClassId })
        setGroupOptions(toOptions((grp as any)?.data || (grp as any)?.groups))
      } catch {
        setGroupOptions([])
      } finally {
        setLoadingGroups(false)
      }
    })()
  }, [systemId, draftClassId])

  // Switching the campus's current session (top-right selector) invalidates
  // whatever class/section/group was picked for the OLD session — without
  // this, the student list silently used stale IDs and looked "broken" until
  // the section was manually reselected.
  useEffect(() => {
    setDraftClassId("")
    setDraftSectionId("")
    setDraftGroupId("")
    setAppliedFilters({ classId: "", sectionId: "", groupId: "" })
    setStudents([])
    setSelectedIds([])
    setHasSearched(false)
  }, [currentSession?._id])

  const fetchStudents = async () => {
    if (!currentSession?._id) return
    setLoading(true)
    try {
      const res = await getEnrollments({
        session: currentSession._id,
        system: systemId,
        class: appliedFilters.classId || undefined,
        section: appliedFilters.sectionId || undefined,
        group: appliedFilters.groupId || undefined,
        page: 1,
        limit: ALL_STUDENTS_LIMIT,
        // Promoting a student who is off the rolls (TC'd, left without
        // information) makes no sense — struck-off stays, since the school
        // hasn't closed that account and may still want them class-listed.
        statusScope: "onroll",
      } as any)
      setStudents(res.data || [])
      setSelectedIds([])
    } catch {
      toast.error("Failed to load students")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!hasSearched) return
    fetchStudents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSearched, appliedFilters])

  // System and Class are required; Section and Group are optional.
  const canSearch = Boolean(systemId && draftClassId)

  const handleSearchClick = () => {
    if (!canSearch) return
    setAppliedFilters({
      classId: draftClassId,
      sectionId: draftSectionId,
      groupId: draftGroupId,
    })
    setSelectedIds([])
    setHasSearched(true)
  }

  const handleResetClick = () => {
    setDraftClassId("")
    setDraftSectionId("")
    setDraftGroupId("")
    setAppliedFilters({ classId: "", sectionId: "", groupId: "" })
    setStudents([])
    setSelectedIds([])
    setSearchText("")
    setHasSearched(false)
  }

  // Target classes = all classes (classes are global, same list every session).
  const loadTargetLists = (_sessionId: string) => {
    setTClassOptions(classOptions)
  }

  const onTargetClass = async (opt: Option) => {
    setTClass(opt)
    setTSection(BLANK)
    setTGroup(BLANK)
    setTGroupOptions([])
    // Target sections = the sections defined for this class in the class modal.
    // We offer them by NAME; the backend re-creates the picked section for the
    // target session (sections are per-session).
    const cls = classesRaw.find((c) => c._id === opt.value)
    const names: string[] = Array.from(
      new Set(((cls?.sections as any[]) || []).map((s) => s?.name).filter(Boolean))
    )
    setTSectionOptions(names.map((n) => ({ value: n, label: n })))
    if (!opt.value) return
    // Group is only required when the target class actually has groups (Grade 9+).
    try {
      const grp = await listGroups({ system: systemId, class: opt.value })
      setTGroupOptions(toOptions((grp as any)?.data || (grp as any)?.groups))
    } catch {
      setTGroupOptions([])
    }
  }

  // Allow typing a brand-new section name too (not yet on the class).
  const handleCreateTargetSection = (name: string) => {
    const trimmed = name.trim()
    if (!trimmed) return
    const opt: Option = { label: trimmed, value: trimmed }
    setTSectionOptions((prev) => [...prev, opt])
    setTSection(opt)
  }

  const openPromote = () => {
    if (!selectedIds.length) {
      toast.warning("Select at least one student to promote")
      return
    }
    setTSession(BLANK)
    setTClass(BLANK)
    setTSection(BLANK)
    setTGroup(BLANK)
    setTClassOptions([])
    setTSectionOptions([])
    setTGroupOptions([])
    setShowModal(true)
  }

  const doPromote = async () => {
    if (!tSession.value || !tClass.value || !tSection.value) {
      toast.warning("Choose target session, class and section")
      return
    }
    if (tGroupOptions.length && !tGroup.value) {
      toast.warning("This class needs a Group")
      return
    }
    setPromoting(true)
    try {
      const payload = {
        enrollmentIds: selectedIds,
        targetSessionId: tSession.value,
        targetClassId: tClass.value,
        targetSectionName: tSection.value,
        targetGroupId: tGroup.value || undefined,
      }
      const res = await promoteBulk(payload)
      if (res.code === "CONFIRM_ALREADY_PROMOTED") {
        setConfirmPromote({ message: res.message, details: res.details || [], payload })
        return
      }
      toast.success(res.message || "Promoted")
      setShowModal(false)
      fetchStudents()
    } catch (err: any) {
      const data = err?.response?.data
      if (data?.code === "BACKWARD_PROMOTION") {
        const names = (data.details || []).map((d: any) => `${d.grNumber} — ${d.name}`).join(", ")
        toast.error(`${data.message}${names ? ` (${names})` : ""}`)
      } else {
        toast.error(data?.message || "Failed to promote")
      }
    } finally {
      setPromoting(false)
    }
  }

  const handleConfirmPromote = async () => {
    if (!confirmPromote) return
    setPromoting(true)
    try {
      const res = await promoteBulk({ ...confirmPromote.payload, confirmUpdate: true })
      toast.success(res.message || "Promoted")
      setConfirmPromote(null)
      setShowModal(false)
      fetchStudents()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to promote")
    } finally {
      setPromoting(false)
    }
  }

  const columns = [
    { title: "GR No", render: (_: unknown, r: Enrollment) => r.student?.grNumber || "N/A" },
    {
      title: "Name",
      render: (_: unknown, r: Enrollment) => (
        <div>
          <div>{`${r.user?.firstName || ""} ${r.user?.lastName || ""}`.trim() || "N/A"}</div>
          {r.promotedToSession && (
            <span className="badge badge-soft-info fs-10">
              Promoted to {r.promotedToSession.title || "next session"}
            </span>
          )}
          {!r.promotedToSession && r.promotedFromSession && (
            <span className="badge badge-soft-info fs-10">
              Promoted from {r.promotedFromSession.title || "previous session"}
            </span>
          )}
        </div>
      ),
    },
    { title: "Class", render: (_: unknown, r: Enrollment) => r.class?.name || "N/A" },
    { title: "Section", render: (_: unknown, r: Enrollment) => r.section?.name || "N/A" },
    // Only shown for classes that actually have groups (Grade 9+) — same
    // condition already used to show/hide the Group filter dropdown.
    ...(groupOptions.length > 0
      ? [{ title: "Group", render: (_: unknown, r: Enrollment) => (r as any).group?.name || "—" }]
      : []),
  ]

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Student Promotion</h3>
            <p className="text-muted mb-0">
              Promote students to a new session. Their current-session record is kept as history.
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-header pb-3 d-flex align-items-start justify-content-between flex-wrap gap-2">
            <div>
              <h4 className="mb-1">Select Students</h4>
              <p className="text-muted small mb-3">
                Choose System and Class (Section and Group are optional), then click{" "}
                <strong>Search</strong> to load matching students. No records show until you search.
              </p>
            </div>

            <div className="d-flex align-items-end flex-wrap w-100">
              <div className="mb-3 me-2" style={{ minWidth: 180 }}>
                <label className="form-label fw-semibold">System</label>
                <CommonSelect
                  className="select"
                  options={systemOptions}
                  value={systemOptions.find((o) => o.value === systemId) || BLANK}
                  onChange={(o) => {
                    const opt = (o as Option) || BLANK
                    setSystemId(opt.value)
                    setDraftClassId("")
                    setDraftSectionId("")
                    setDraftGroupId("")
                    setAppliedFilters({ classId: "", sectionId: "", groupId: "" })
                    setStudents([])
                    setSelectedIds([])
                    setHasSearched(false)
                  }}
                />
              </div>

              <div className="mb-3 me-2" style={{ minWidth: 180 }}>
                <label className="form-label fw-semibold">Class</label>
                <CommonSelect
                  className="select"
                  options={classOptions}
                  value={classOptions.find((opt) => opt.value === draftClassId)}
                  onChange={(o) => {
                    const opt = (o as Option) || BLANK
                    setDraftClassId(opt.value)
                    setDraftSectionId("")
                    setDraftGroupId("")
                  }}
                  placeholder={!systemId ? "Select System first" : "Select Class"}
                  isDisabled={!systemId}
                />
              </div>

              <div className="mb-3 me-2" style={{ minWidth: 180 }}>
                <label className="form-label fw-semibold">
                  Section <span className="text-muted fw-normal">(optional)</span>
                </label>
                <CommonSelect
                  // Remounts whenever the class (or a reset) changes the
                  // available sections, so react-select can never hang onto
                  // a previous class's selected option internally.
                  key={`section-${draftClassId}`}
                  className="select"
                  options={sectionOptions}
                  value={sectionOptions.find((opt) => opt.value === draftSectionId) || null}
                  onChange={(o) => setDraftSectionId((o as Option)?.value || "")}
                  placeholder={
                    !draftClassId
                      ? "Select Class first"
                      : sectionOptions.length === 0
                        ? "No sections"
                        : "All sections"
                  }
                  isDisabled={!draftClassId || sectionOptions.length === 0}
                />
              </div>

              {groupOptions.length > 0 && (
                <div className="mb-3 me-2" style={{ minWidth: 180 }}>
                  <label className="form-label fw-semibold">
                    Group <span className="text-muted fw-normal">(optional)</span>
                  </label>
                  <CommonSelect
                    key={`group-${draftClassId}`}
                    className="select"
                    options={groupOptions}
                    value={groupOptions.find((opt) => opt.value === draftGroupId) || null}
                    onChange={(o) => setDraftGroupId((o as Option)?.value || "")}
                    placeholder={loadingGroups ? "Loading..." : "All groups"}
                    isDisabled={loadingGroups}
                  />
                </div>
              )}

              <div className="mb-3 d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSearchClick}
                  disabled={!canSearch}
                  title={canSearch ? "Search students" : "Select System and Class first"}
                >
                  <i className="ti ti-search me-1" />
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleResetClick}
                  title="Reset filters"
                >
                  <i className="ti ti-refresh" />
                </button>
              </div>
            </div>

            {!canSearch && (
              <div className="small text-muted mt-2">
                <i className="ti ti-info-circle me-1" />
                Select System and Class to search — Section and Group are optional.
              </div>
            )}

            {hasSearched && (
              <div className="d-flex justify-content-between align-items-center mt-3 w-100">
                <span className="text-muted small">
                  {loading
                    ? "Loading…"
                    : `${filteredStudents.length} student${filteredStudents.length === 1 ? "" : "s"} found${
                        selectedIds.length ? `, ${selectedIds.length} selected` : ""
                      }`}
                </span>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openPromote}
                  disabled={!selectedIds.length}
                >
                  Promote {selectedIds.length ? `(${selectedIds.length})` : ""}
                </button>
              </div>
            )}
          </div>

          <div className="card-body p-0 py-3">
            {hasSearched ? (
              <Table
                dataSource={filteredStudents}
                columns={columns}
                loading={loading}
                Selection={true}
                setSearchText={setSearchText}
                onSelectionChange={(keys: any[]) => setSelectedIds((keys || []).map((k) => String(k)))}
                hidePagination
              />
            ) : (
              <div className="text-center py-5 text-muted">
                <i className="ti ti-list-search" style={{ fontSize: 36 }} />
                <p className="mt-2 mb-0">
                  Select System and Class above, then click <strong>Search</strong> to view students.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Promote modal */}
      {showModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Promote {selectedIds.length} student(s)</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <label className="form-label">Target Session</label>
                <CommonSelect
                  className="select mb-3"
                  options={[BLANK, ...sessionOptions]}
                  value={tSession}
                  onChange={(o) => {
                    const opt = (o as Option) || BLANK
                    setTSession(opt)
                    setTClass(BLANK)
                    setTSection(BLANK)
                    setTGroup(BLANK)
                    setTGroupOptions([])
                    loadTargetLists(opt.value)
                  }}
                />
                <label className="form-label">Target Class</label>
                <CommonSelect
                  className="select mb-3"
                  options={[BLANK, ...tClassOptions]}
                  value={tClass}
                  onChange={(o) => onTargetClass((o as Option) || BLANK)}
                />
                <label className="form-label">Target Section</label>
                <CreatableSelect
                  className="mb-3"
                  classNamePrefix="react-select"
                  options={tSectionOptions}
                  value={tSection.value ? tSection : null}
                  onChange={(o) => setTSection((o as Option) || BLANK)}
                  onCreateOption={handleCreateTargetSection}
                  isDisabled={!tClass.value}
                  placeholder={tClass.value ? "Select a section (or type to add)" : "Pick target class first"}
                  formatCreateLabel={(input: string) => `Add section "${input}"`}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                  // Must beat .modal's z-index (10105 — see _header.scss) or
                  // a portaled menu opened inside a modal renders invisibly behind it.
                  styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 10110 }) }}
                />
                {tGroupOptions.length > 0 && (
                  <>
                    <label className="form-label">Group (required for this class)</label>
                    <CommonSelect
                      className="select mb-3"
                      options={[BLANK, ...tGroupOptions]}
                      value={tGroup}
                      onChange={(o) => setTGroup((o as Option) || BLANK)}
                    />
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" disabled={promoting} onClick={() => doPromote()}>
                  {promoting ? "Promoting…" : "Promote"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Already-promoted confirmation */}
      {confirmPromote && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center">
                <span className="delete-icon">
                  <i className="ti ti-alert-circle" />
                </span>
                <h4>Already Promoted</h4>
                <p className="mb-3">{confirmPromote.message}</p>
                <ul className="list-unstyled text-start mb-3">
                  {confirmPromote.details.map((d) => (
                    <li key={d.enrollmentId} className="mb-1">
                      {d.grNumber} — {d.name}
                      {d.targetClass && (
                        <div className="text-muted fs-12">
                          Currently at: {d.targetClass}
                          {d.targetSection ? `, Section ${d.targetSection}` : ""}
                          {d.targetGroup ? `, ${d.targetGroup}` : ""}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn btn-light me-3"
                    disabled={promoting}
                    onClick={() => setConfirmPromote(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={promoting}
                    onClick={() => handleConfirmPromote()}
                  >
                    {promoting ? "Updating…" : "Yes, Update"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="d-none">
        <Link to={routes.studentList}>students</Link>
      </div>
    </div>
  )
}

export default StudentPromotion
